import { inject, Service } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthToken } from '../types/auth-token';
import { BelvotoUrlData } from '../types/belvoto-url-data';
import { DownloadFileTypeEnum } from '../enums/download-file-type.enum';
import { AnlageplannungFileData } from '../types/anlageplannung-file-data';
import { NinCategoryItemDto } from '../types/nin-category-item-dto';
import { CockpitStatisticsDto } from '../types/cockpit-statistics-dto';
import { CockpitHistoryDataDto } from '../types/cockpit-history-data-dto';
import { CockpitOverTimeDto } from '../types/cockpit-over-time-dto';
import { YearsGetDto } from '../types/years-get-dto';
import { CockpitHistoryDataMemberDto } from '../types/cockpit-history-data-member-dto';

@Service()
export class ApiService {
  private baseUrl = environment.toolboxApiEndpoint;
  private httpClient = inject(HttpClient);

  createAccessToken(
    code: string,
    codeVerifier: string,
    redirectUri: string,
  ): Observable<AuthToken> {
    return this.httpClient.post<AuthToken>(`${this.baseUrl}/auth/token`, {
      code,
      codeVerifier,
      redirectUri,
    });
  }

  downloadAnlageplanungFiles(language: string, type: DownloadFileTypeEnum) {
    return this.httpClient.get(`${this.baseUrl}/anlageplanungs/download`, {
      responseType: 'blob',
      params: {
        lang: language,
        type,
      },
    });
  }

  getAnlageplanungFiles() {
    return this.httpClient.get<AnlageplannungFileData[]>(`${this.baseUrl}/anlageplanungs`);
  }

  getBelvotoUrl(firstName: string, lastName: string) {
    return this.httpClient.get<BelvotoUrlData>(`${this.baseUrl}/belvoto/embed`, {
      params: {
        firstName,
        lastName,
      },
    });
  }

  getCockpitOverTimeInfo(year: string, applicationName: string | undefined, userType: string | undefined) {
    const params: Record<string, string> = {
      year
    };
    if(applicationName) {
      params['applicationName'] = applicationName;
    }
    if (userType) {
      params['userType'] = userType;
    }
    return this.httpClient.get<CockpitOverTimeDto>(`${this.baseUrl}/login-histories/over-time`, {
      params: params
    });
  }

  getCockpitStatistics(year: string, applicationName: string | undefined, userType: string | undefined) {
    const params: Record<string, string> = {
      year
    };
    if (applicationName) {
      params['applicationName'] = applicationName;
    }
    if (userType) {
      params['userType'] = userType;
    }
    return this.httpClient.get<CockpitStatisticsDto>(`${this.baseUrl}/login-histories/stats`, {
      params: params
    });
  }

  getDataYears() {
      return this.httpClient.get<YearsGetDto>(`${this.baseUrl}/login-histories/years`);
  }

  private getParamsKeyInfo(
    key: string,
    data: Record<string, string | number | boolean | undefined>,
  ) {
    const output: { [param: string]: string | number } = {};
    // @ts-ignore
    if (data[key] !== undefined) {
      // @ts-ignore
      output[key] = data[key];
    }

    return output;
  }

  getCockpitHistoryTableData(data: Record<string, string | number | boolean | undefined>) {
    const params: { [param: string]: string | number } = {
      page: data['page'] as number,
      itemsPerPage: data['itemsPerPage'] as number,
      ...this.getParamsKeyInfo('userType', data),
      ...this.getParamsKeyInfo('applicationName', data),
      ...this.getParamsKeyInfo('sort', data),
      ...this.getParamsKeyInfo('year', data),
      ...this.getParamsKeyInfo('search', data),
    };
    return this.httpClient.get<CockpitHistoryDataDto>(`${this.baseUrl}/login-histories`, {
      params,
      headers: new HttpHeaders({
        accept: 'application/ld+json',
      }),
    });
  }

  getNinHistoryCategories() {
    return this.httpClient.get<NinCategoryItemDto[]>(`${this.baseUrl}/nin_histories`, {
      params: {
        page: 1,
        itemsPerPage: 100,
      },
    });
  }

  getNinCategoryImage(category: string, language: string) {
    return this.httpClient.get(`${this.baseUrl}/ninhistory/download`, {
      responseType: 'blob',
      params: {
        lang: language,
        name: category,
      },
    });
  }

  refreshAccessToken(refreshToken: string): Observable<AuthToken> {
    return this.httpClient.post<AuthToken>(`${this.baseUrl}/auth/refresh`, {
      refresh_token: refreshToken,
    });
  }

  createLoginHistory(idToken: string, applicationName: string): Observable<CockpitHistoryDataMemberDto> {
    return this.httpClient.post<CockpitHistoryDataMemberDto>(
      `${this.baseUrl}/login_histories`,
      { idToken, applicationName }
    );
  }
}
